import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Accessibility preferences, kept outside AuthContext on purpose: they have to
// work on the sign-in screen, before anyone is logged in, and they belong to
// the device rather than to the account.
const STORAGE_KEY = "a11y-preferences";

const DEFAULTS = {
  highContrast:  false,
  textScale:     100,     // 100 | 125 | 150 (WCAG 1.4.4 asks for 200% via browser zoom too)
  reduceMotion:  false,
  largeTargets:  false,
  colorBlindSafe: false,
};

function systemDefaults() {
  if (typeof window === "undefined" || !window.matchMedia) return DEFAULTS;
  // Somebody who already told their operating system what they need should
  // not have to tell the portal a second time.
  return {
    ...DEFAULTS,
    highContrast: window.matchMedia("(prefers-contrast: more)").matches,
    reduceMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  };
}

function load() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (stored && typeof stored === "object") return { ...systemDefaults(), ...stored };
  } catch { /* corrupt or absent — fall through to the system's own answer */ }
  return systemDefaults();
}

const A11yContext = createContext(null);

export function A11yProvider({ children }) {
  const [prefs, setPrefs] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); } catch { /* private mode */ }

    // Every style in this app is an inline JS object, which beats any
    // stylesheet — so a theme cannot be swapped with CSS alone. What can be
    // swapped is the value behind a custom property, and the palette in
    // Shared.jsx is expressed with those. These attributes are the switch.
    const root = document.documentElement;
    root.dataset.a11yContrast  = prefs.highContrast ? "high" : "normal";
    root.dataset.a11yMotion    = prefs.reduceMotion ? "reduced" : "normal";
    root.dataset.a11yTargets   = prefs.largeTargets ? "large" : "normal";
    root.dataset.a11yPalette   = prefs.colorBlindSafe ? "colorblind-safe" : "brand";
    root.style.setProperty("--dp-scale", String(prefs.textScale / 100));
  }, [prefs]);

  const value = useMemo(() => ({
    prefs,
    set: (key, val) => setPrefs(p => ({ ...p, [key]: val })),
    reset: () => setPrefs(systemDefaults()),
    anyEnabled: prefs.highContrast || prefs.reduceMotion || prefs.largeTargets
      || prefs.colorBlindSafe || prefs.textScale !== 100,
  }), [prefs]);

  return <A11yContext.Provider value={value}>{children}</A11yContext.Provider>;
}

export function useA11y() {
  const ctx = useContext(A11yContext);
  if (!ctx) throw new Error("useA11y must be used inside A11yProvider");
  return ctx;
}
